import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { formatMoneyRange } from '../../../../lib/format/money';
/** Tile explanations (SpecraMetric.info is required on every KPI tile). */ export const KPI_INFO = {
    decision: 'The next go/no-go gate scheduled on this pursuit (a review marked as a gate)',
    rfi: 'Questions your team has marked answered, on the open RFI response due soonest',
    contract: 'End of the last period of performance, options included, on the contract this pursuit recompetes',
    blackhat: "Range from the latest black hat's priced scenarios (low and high, or min and max)"
};
/** "YYYY-MM-DD" or RFC3339 → local date (a bare date never shifts a day by time zone). */ function day(value) {
    return parseISO(value);
}
function plural(n, unit) {
    return `${n} ${unit}${n === 1 ? '' : 's'}`;
}
/**
 * The pursuit Home KPI strip (GRW-16 item 3), in order decision, rfi,
 * contract, black hat. A metric whose source is unset is skipped, so an
 * existing pursuit with nothing scheduled or linked returns [].
 */ export function buildPursuitKpis(header, now) {
    if (!header) return [];
    const id = header.captureProjectId;
    const metrics = [];
    const decision = header.nextDecision;
    if (decision?.dueAt) {
        const due = day(decision.dueAt);
        const n = differenceInCalendarDays(due, now);
        const when = n > 0 ? `in ${plural(n, 'day')}` : n === 0 ? 'today' : `overdue ${plural(-n, 'day')}`;
        metrics.push({
            id: 'decision',
            label: `${decision.gateLabel || 'Gate'} go / no-go`,
            value: format(due, 'd MMM'),
            note: decision.deciderDisplayName ? `${when} · ${decision.deciderDisplayName}` : when,
            tone: n < 0 ? 'danger' : 'warning',
            href: `/capture/${id}/decisions`,
            info: KPI_INFO.decision
        });
    }
    const rfi = header.rfiProgress;
    if (rfi) {
        const due = rfi.dueAt ? day(rfi.dueAt) : null;
        metrics.push({
            id: 'rfi',
            label: `${rfi.shortLabel} answers`,
            value: `${rfi.answeredCount} / ${rfi.totalCount}`,
            // ONLYOFFICE answers have no "final" state GRW can read: never say final.
            note: due ? `marked answered · due ${format(due, 'EEE d MMM')}` : 'marked answered',
            tone: due && differenceInCalendarDays(due, now) < 0 ? 'danger' : 'info',
            href: rfi.href || undefined,
            info: KPI_INFO.rfi
        });
    }
    const clock = header.contractClock;
    if (clock?.ultimateEnd) {
        const end = day(clock.ultimateEnd);
        const months = clock.monthsToUltimateEnd;
        const days = differenceInCalendarDays(end, now);
        metrics.push({
            id: 'contract',
            label: clock.source === 'opportunity_signal' ? 'Incumbent contract ends' : 'Our contract ends',
            value: days < 0 ? 'Ended' : months >= 1 ? `${months} mo` : plural(days, 'day'),
            note: `PoP ends ${format(end, 'd MMM yyyy')}`,
            tone: months <= 12 ? 'danger' : months <= 24 ? 'warning' : 'neutral',
            href: clock.programId ? `/delivery/programs/${clock.programId}` : undefined,
            info: KPI_INFO.contract
        });
    }
    const blackHat = header.blackHat;
    if (blackHat) {
        const period = blackHat.periodMonths === 0 ? '' : blackHat.periodMonths % 12 === 0 ? plural(blackHat.periodMonths / 12, 'year') : plural(blackHat.periodMonths, 'month');
        const version = blackHat.createdAt ? `v${blackHat.version}, ${format(parseISO(blackHat.createdAt), 'd MMM')}` : `v${blackHat.version}`;
        metrics.push({
            id: 'blackhat',
            label: `Black hat · ${blackHat.companyName}`,
            value: blackHat.basis === 'none' ? 'Not priced' : formatMoneyRange(blackHat.lowCents, blackHat.highCents),
            note: [
                period,
                version
            ].filter(Boolean).join(' · '),
            tone: 'neutral',
            href: `/capture/${id}/black-hat`,
            info: KPI_INFO.blackhat
        });
    }
    return metrics;
}
